import * as React from "react";
import {
  Typography,
  Button,
  MenuItem,
  Theme,
  makeStyles,
  createStyles,
  Menu,
  Divider,
  Hidden,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  CircularProgress,
  DialogActions,
} from "@material-ui/core";
import { SessionCache } from "../../helpers/cache";
import { useHistory } from "react-router-dom";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import UserPicture from "../UserPicture";
import { FETCH_ALL_NON_ADMIN_USERS, FETCH_USER_INFO, FETCH_USER_INFO_BY_ID } from "../../Fedx";
import { useHubx } from "../../HubX";
import IUserResponse from "../../core/contracts/IUserResponse";
import { Skeleton } from "@material-ui/lab";
import SmartSelect from "../../features/SmartFilters/SmartSelect";
import { SelectOptions } from "../../core/models/SelectOptions";
import IUserResponseDTO from "../../core/contracts/IUserResponseDTO";
import { useSnackbar } from "notistack";
import useConstructor from "../../core/hooks/useConstructor";
import useAuth from "../../core/hooks/useAuth";
import { ADMIN, SUPER_ADMIN } from "../../core/models/GroupTypeEnum";

type Props = { name?: string };

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonRoot: {
      "height": theme.mixins.toolbar.minHeight,

      "& .MuiButton-label": {
        height: "100%",
      },

      "& .nameWrapper": {
        "display": "flex",
        "alignItems": "center",
        "marginRight": theme.spacing(1),

        "& .divider": {
          height: "2em",
          marginRight: theme.spacing(2),
        },
      },
    },

    onSeeAsModeText: {
      color: theme.palette.error.light,
      fontWeight: 700,
    },

    onSeeAsModeImage: {
      background: theme.palette.error.light,
    },

    searchWrapper: {
      width: "550px",
      padding: theme.spacing(2),
    },
  })
);

function UserMenu(props: Props) {
  const hub = useHubx();
  const toastr = useSnackbar();

  const { name } = props;
  const classes = useStyles();

  const authProvider = useAuth();
  const lCache = new SessionCache();
  const history = useHistory();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [seeAsModalOpen, setSeeAsModalOpen] = React.useState<boolean>(false);
  const [isLoadingSeeAsResponse, setIsLoadingSeeAsResponse] = React.useState<boolean>(false);
  const [isLoadingUsers, setIsLoadingUsers] = React.useState<boolean>(false);
  const [users, setUsers] = React.useState<SelectOptions[]>([]);
  const [username, setUsername] = React.useState<string | undefined>(name);
  const [selectedOption, setSelectedOption] = React.useState<null | SelectOptions>(null);
  const [onSeeAsMode, setOnSeeAsMode] = React.useState<boolean>(false);
  const [seeAsModeAvailable, setSeeAsModeAvailable] = React.useState<boolean>(false);

  useConstructor(() => {
    const sessionCache = new SessionCache();
    const user: IUserResponse | null = sessionCache.get("user");
    setOnSeeAsMode(user?.onSeeAsMode ?? false);

    if (
      user?.onSeeAsMode ||
      user?.installationGroup?.find((x) => x.groupType === ADMIN || x.groupType === SUPER_ADMIN)
    ) {
      setSeeAsModeAvailable(true);
    }
  });

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUpdateDefinitions = () => {
    authProvider.updateUserDefinitions();
    handleClose();
    history.go(0);
  };

  const handleClickLogout = () => {
    lCache.clear();
    if (window?.HAS_CONFIGURED_G_ANALYTICS) {
      window.HAS_CONFIGURED_G_ANALYTICS = undefined;
    }
    // Redirect to initial page
    history.push("/");
  };

  const handleClickSeeAs = async () => {
    setIsLoadingUsers(true);
    setSeeAsModalOpen(true);
    handleClose();
    try {
      const APIResponseUsers: IUserResponseDTO[] = await hub.request(FETCH_ALL_NON_ADMIN_USERS);
      const usersResponse: SelectOptions[] = APIResponseUsers.map((user: IUserResponseDTO) => ({
        id: user.id ?? 0,
        label: user.email ?? "",
        value: user.email ?? "",
        name: user.name ?? "",
      }));
      setUsers(usersResponse);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("SearchUsersPage -> error", error);
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar obter as informações dos usuários cadastrados!",
        { variant: "error" }
      );
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleClickExitSeeAs = async () => {
    handleClose();
    const sessionCache = new SessionCache();
    const result: IUserResponse = await hub.request(FETCH_USER_INFO);
    sessionCache.set("user", result);
    setUsername(result.name);
    setOnSeeAsMode(false);
    history.go(0);
  };

  const handleClickConfirmSeeAs = async () => {
    if (selectedOption !== null) {
      setIsLoadingSeeAsResponse(true);
      const sessionCache = new SessionCache();
      const user: IUserResponse | null = sessionCache.get("user");
      const result: IUserResponse = await hub.request(FETCH_USER_INFO_BY_ID, selectedOption.id);
      result.name = `${user?.name?.split(" ")[0]} (Vendo como ${
        result.name === "" ? result.username : result.name
      })`;
      result.onSeeAsMode = true;
      sessionCache.set("user", result);
      setUsername(result.name);
      setSeeAsModalOpen(false);
      setOnSeeAsMode(true);
      history.go(0);
      setIsLoadingSeeAsResponse(false);
    }
  };

  function customSelectLabelRender(option: SelectOptions) {
    if (option.name) {
      return `${option.name} (E-mail: ${option.label})`;
    }

    return option.label;
  }

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        className={classes.buttonRoot}
        onClick={handleToggle}
        aria-label="Menu do Usuário"
        aria-haspopup="true"
      >
        <div className={"nameWrapper"}>
          <Hidden xsDown>
            <Divider className={"divider"} orientation="vertical" />
            <Typography className={onSeeAsMode ? classes.onSeeAsModeText : ""} variant="body1">
              {username}
            </Typography>
          </Hidden>
        </div>

        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        {onSeeAsMode ? (
          <UserPicture className={classes.onSeeAsModeImage} name={username} />
        ) : (
          <UserPicture name={username} />
        )}
      </Button>
      <Menu
        id="menu-appbar"
        keepMounted
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        elevation={0}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {onSeeAsMode && (
          <MenuItem onClick={handleClickExitSeeAs}>Desfazer visualização simulada</MenuItem>
        )}
        <MenuItem onClick={handleUpdateDefinitions}>Atualizar definições</MenuItem>
        {seeAsModeAvailable && <MenuItem onClick={handleClickSeeAs}>Ver como...</MenuItem>}
        <MenuItem onClick={handleClickLogout}>Log Out</MenuItem>
      </Menu>

      {/* See As dialog */}
      <Dialog open={seeAsModalOpen}>
        <DialogTitle>Ver como outro usuário</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {/* Search Field */}
            <div className={classes.searchWrapper}>
              {"Selecione um usuário"}
              {isLoadingUsers ? (
                <Skeleton height={40} />
              ) : (
                <SmartSelect
                  isMulti={false}
                  options={users}
                  getOptionLabel={customSelectLabelRender}
                  value={selectedOption}
                  onChange={setSelectedOption}
                />
              )}
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={isLoadingSeeAsResponse} onClick={() => setSeeAsModalOpen(false)}>
            Cancelar
          </Button>

          {isLoadingSeeAsResponse ? (
            <CircularProgress />
          ) : (
            <Button variant="contained" onClick={() => handleClickConfirmSeeAs()}>
              Confirmar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UserMenu;
